import type { ReactNode } from "react";
import { Box } from "../../../components/ui/box";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  emptyStateVariants,
} from "../../../components/ui/empty";
import type { Omittable } from "../../../lib/props";

/**
 * The one empty state every widget body uses: words alone, centered — a
 * muted title naming what is absent (and the window it was looked for in),
 * an optional reason under it, and an optional door. No glyph, no sketch: a
 * board of cards each drawing its own art for "nothing" reads as decoration
 * competing with the cards that do have data, and the title already says
 * everything the art restated. Sized `sm` to sit inside a widget; the
 * maximized/full view can pass `size="default"`. Pass `action` for a CTA.
 */
export function WidgetEmpty({
  title,
  description,
  size = "sm",
  action,
}: Omittable<{
  title: string;
  description?: string;
  size?: "sm" | "default";
  action?: ReactNode;
}>) {
  const sm = size === "sm";
  return (
    <Box fill align="center" justify="center">
      <Empty className={emptyStateVariants({ size })}>
        <EmptyHeader>
          <EmptyTitle className={sm ? "text-sm font-medium text-muted-foreground" : undefined}>
            {title}
          </EmptyTitle>
          {description ? (
            <EmptyDescription className={sm ? "max-w-[280px]" : undefined}>
              {description}
            </EmptyDescription>
          ) : null}
        </EmptyHeader>
        {action ? <EmptyContent>{action}</EmptyContent> : null}
      </Empty>
    </Box>
  );
}
